import * as React from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import { Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@mui/material/Typography";
import CustomBigSideMenuCard from "./mainGridComponents/bigSideMenuCard.components";
import CustomSmallSideMenuCard from "./mainGridComponents/smallSideMenuCard.components";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
  setDTFarm,
  setDTField,
  setLivestock,
  setPlants,
  setSingleLivestock,
  setSinglePlant,
} from "../../../store/features/digitalTwin/selectedDigitalTwinSlice";
import { calculatePolygonAreaForArray } from "../../muiTabs/utils";
import { fetchDTFieldsStart } from "../../../store/features/digitalTwin/dTFieldsSlice";
import { fetchPlantsStart } from "../../../store/features/digitalTwin/plantsSlice";
import { fetchPlantsMeasurementsStart } from "../../../store/features/digitalTwin/plantsMeasurementsSlice";
import { fetchDigitalTwinStart } from "../../../store/features/digitalTwin/digitalTwinSlice";
import {
  fetchFarmsStart,
  fetchLivestockDigitalTwinStart,
  fetchLivestockDigitalTwinsStart,
  fetchLivestockMeasurementsStart,
  fetchLivestockStart,
} from "../../../store/features/digitalTwin/farms/farmsSlice";

const drawerWidth = 400;

const getApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getFarmName = (farm) =>
  farm?.farm_name || farm?.name || farm?.farmName || `Farm ${farm?.id ?? "-"}`;

const getFarmArea = (farm) =>
  Number(farm?.area || farm?.surface || farm?.farm_area || 0);

const getFarmId = (farm) => farm?.id || farm?.farm_id;

const getFieldArea = (field) => {
  const coordinates = field?.geom?.coordinates?.[0];
  return coordinates ? calculatePolygonAreaForArray(coordinates) : 0;
};

const getFieldId = (field) => field?.id || field?.field_id;

const getFarmType = (farm) =>
  farm?.farm_type ||
  farm?.animal_type ||
  farm?.livestock_type ||
  "Allevamento";

const getLivestockId = (livestock) =>
  livestock?.id || livestock?.livestock_id;

const getLivestockName = (livestock) =>
  livestock?.livestock_name ||
  livestock?.name ||
  livestock?.animal_type ||
  livestock?.livestock_type ||
  `Capo ${livestock?.id ?? "-"}`;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: "border-box",
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: "border-box",
  },
});

export default function DigitalTwinSideMenu() {
  const [chooseTerrain, setChooseTerrain] = React.useState(false);
  const [choosenTerrainIndex, setChooseTerrainIndex] = React.useState(null);
  const [choosenFarmIndex, setChooseFarmIndex] = React.useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fieldsData = getApiList(useSelector((state) => state.dTFields?.data));
  const plantsData = getApiList(useSelector((state) => state.plants?.data));
  const plantsMeasurementsData = getApiList(
    useSelector((state) => state.plantsMeasurements?.data),
  );
  const selectedField = useSelector((state) => state.selectedDigitalTwin.field);
  const selectedFarm = useSelector((state) => state.selectedDigitalTwin.farm);
  const farmsData = getApiList(useSelector((state) => state.farms?.farms?.data));
  const livestockData = getApiList(
    useSelector((state) => state.farms?.livestock?.data),
  );
  
  const { isLoading } = useSelector((state) => state.dTFields);
  const { loadingPlants } = useSelector((state) => state.plants);
  const farmsLoading = useSelector((state) => state.farms?.farms?.isLoading);
  const livestockLoading = useSelector(
    (state) => state.farms?.livestock?.isLoading,
  );

  const getPlantTypeFromMeasurements = (field) => {
    const fieldId = getFieldId(field);
    const plantTypes = plantsMeasurementsData
      .filter((measurement) => {
        if (!fieldId) return true;
        const measurementFieldId = measurement?.field_id || measurement?.field;
        return (
          !measurementFieldId ||
          String(measurementFieldId) === String(fieldId)
        );
      })
      .map((measurement) => measurement?.plant_type)
      .filter(Boolean);

    return [...new Set(plantTypes)].join(", ");
  };

  /* when i select/deselect a card I load or reset the data */
  const handleCardSelected = (index) => {
    navigate(`/digitalTwin`);

    if (index === null) {
      dispatch(setDTField(null));
      dispatch(setDTFarm(null));
      dispatch(setPlants(null));
      dispatch(setLivestock(null));
      setChooseTerrain(!chooseTerrain);
      setChooseTerrainIndex(null);
      setChooseFarmIndex(null);
    } else {
      const field = fieldsData[index];
      dispatch(setDTField(field));
      dispatch(setDTFarm(null));
      dispatch(setPlants(plantsData));
      dispatch(setLivestock(null));
      setChooseTerrain(!chooseTerrain);
      setChooseTerrainIndex(index);
      setChooseFarmIndex(null);
    }
  };
/* then I do the same for farms */
  const handleFarmSelected = (index) => {
    navigate(`/digitalTwin`);

    if (index === null) {
      dispatch(setDTFarm(null));
      dispatch(setLivestock(null));
      setChooseFarmIndex(null);
    } else {
      const farm = farmsData[index];
      dispatch(setDTField(null));
      dispatch(setDTFarm(farm));
      dispatch(setPlants(null));
      dispatch(setLivestock(livestockData));
      setChooseTerrain(false);
      setChooseTerrainIndex(null);
      setChooseFarmIndex(index);
    }
  };
/* redirect to crop details page */
  const handleRedirect = (crop) => {
    //save selected crop in redux then navigate to page
    dispatch(setSinglePlant(crop));
    navigate(`/digitalTwin/${crop.id}`);
  };
/* same for livestock */
  const handleLivestockSelected = (livestock) => {
    const livestockId = getLivestockId(livestock);

    dispatch(setSingleLivestock(livestock));
    if (!livestockId) return;

    dispatch(fetchLivestockMeasurementsStart({ livestock_id: livestockId }));
    dispatch(fetchLivestockDigitalTwinStart({ livestock_id: livestockId }));
    dispatch(fetchLivestockDigitalTwinsStart({ livestock_id: livestockId }));
    navigate(`/digitalTwin/livestock/${livestockId}`);
  };
/* I update the UI when the selected terrain changes */
  useEffect(() => {
  }, [fieldsData, choosenTerrainIndex]);

  useEffect(() => {
    dispatch(fetchDTFieldsStart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedField) {
      setChooseTerrain(false);
      setChooseTerrainIndex(null);
    } else if (Array.isArray(fieldsData)) {
      const selectedIndex = fieldsData.findIndex(
        (field) => String(field?.id) === String(selectedField?.id),
      );

      if (selectedIndex !== -1) {
        setChooseTerrain(true);
        setChooseTerrainIndex(selectedIndex);
        setChooseFarmIndex(null);
      }
    }
  }, [fieldsData, selectedField]);

  useEffect(() => {
    if (!selectedFarm) {
      setChooseFarmIndex(null);
      return;
    }

    const selectedIndex = farmsData.findIndex(
      (farm) => String(getFarmId(farm)) === String(getFarmId(selectedFarm)),
    );

    if (selectedIndex !== -1) {
      setChooseTerrain(false);
      setChooseTerrainIndex(null);
      setChooseFarmIndex(selectedIndex);
    }
  }, [farmsData, selectedFarm]);
  /*
   */

  useEffect(() => {
    if (choosenTerrainIndex !== null) {
      dispatch(
        fetchPlantsStart({ field_id: fieldsData[choosenTerrainIndex].id }),
      );
      dispatch(
        fetchPlantsMeasurementsStart({
          field_id: fieldsData[choosenTerrainIndex].id,
        }),
      );
      dispatch(
        fetchDigitalTwinStart({
          field_id: fieldsData[choosenTerrainIndex].id,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosenTerrainIndex]);

  useEffect(() => {
    if (choosenFarmIndex !== null) {
      const farmId = getFarmId(farmsData[choosenFarmIndex]);
      if (!farmId) return;

      dispatch(fetchLivestockStart({ farm_id: farmId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosenFarmIndex]);

  useEffect(() => {
    dispatch(fetchFarmsStart());
  }, [dispatch]);

  const margin_top = 14;

  if (loadingPlants || livestockLoading) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          [`& .${drawerClasses.paper}`]: {
            backgroundColor: "background.default",
          },
        }}
      >
        <Box height={100}></Box>
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ my: 1, mx: 2 }} />
      </Drawer>
    );
  }
  if (isLoading || farmsLoading) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          [`& .${drawerClasses.paper}`]: {
            backgroundColor: "background.default",
          },
        }}
      >
        <Box height={100}></Box>
        <Skeleton
          variant="text"
          sx={{ fontSize: "1rem", ml: 2, width: "9vh" }}
        />
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ m: 2 }} />
      </Drawer>
    );
  }

  if (!isLoading && !farmsLoading && !fieldsData?.length && !farmsData?.length){
    return (
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          [`& .${drawerClasses.paper}`]: {
            backgroundColor: "background.default",
          },
        }}
      >
        <Typography
          sx={{ p: 1 }}
          fontSize={"16px"}
          fontWeight={"semibold"}
          color="text.secondary"
        >
          Nessun digital twin
        </Typography>
      </Drawer>
    );
  }
  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", md: "block" },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: "background.default",
        },
      }}
    >
      {choosenTerrainIndex === null && choosenFarmIndex === null ? (
        /* choose one of the cards */
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 150px)",
            display: "flex",
            flexDirection: "column",
            marginTop: margin_top,
            flexGrow: 1,
          }}
        >
          <Typography
            sx={{ p: 1 }}
            fontSize={"16px"}
            fontWeight={"semibold"}
            color="text.secondary"
          >
            Scegli un terreno
          </Typography>
          {fieldsData.map((field, index) => (
            <CustomBigSideMenuCard
              key={field.field_uuid}
              name={field.field_name}
              crop={getPlantTypeFromMeasurements(field)}
              area={getFieldArea(field)}
              iconColor={field.iconColor}
              onClick={() => handleCardSelected(index)}
            />
          ))}

          {farmsData.map((farm, index) => (
            <CustomBigSideMenuCard
              key={getFarmId(farm) || index}
              name={getFarmName(farm)}
              crop={getFarmType(farm)}
              cropLabel="Tipologia"
              area={getFarmArea(farm)}
              onClick={() => handleFarmSelected(index)}
            />
          ))}
        </Box>
      ) : choosenTerrainIndex !== null ? (
        /* card selected, show details */
        <Box
          sx={{
            overflow: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            marginTop: margin_top,
            flexGrow: 1,
          }}
        >
          <CustomBigSideMenuCard
            open
            name={
              choosenTerrainIndex !== null
                ? fieldsData[choosenTerrainIndex].field_name
                : "-"
            }
            crop={getPlantTypeFromMeasurements(selectedField)}
            area={
              choosenTerrainIndex !== null
                ? getFieldArea(fieldsData[choosenTerrainIndex])
                : 0
            }
            onClick={() => handleCardSelected(null)}
          />
          <CustomSmallSideMenuCard
            area={"Area test"}
            number_of_crops={"2"}
            plants={plantsData}
            status="status1"
            onClick={(crop) => handleRedirect(crop)}
          />
        </Box>
      ) : (
        <Box
          sx={{
            overflow: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            marginTop: margin_top,
            flexGrow: 1,
          }}
        >
          <CustomBigSideMenuCard
            open
            name={
              choosenFarmIndex !== null
                ? getFarmName(farmsData[choosenFarmIndex])
                : "-"
            }
            crop={getFarmType(
              selectedFarm || farmsData[choosenFarmIndex],
            )}
            cropLabel="Tipologia"
            area={
              choosenFarmIndex !== null
                ? getFarmArea(farmsData[choosenFarmIndex])
                : 0
            }
            onClick={() => handleFarmSelected(null)}
          />
          <Box p={1} sx={{ overflowY: "auto", flex: 1 }}>
            {livestockData.map((livestock, index) => (
              <Box
                key={livestock?.id || livestock?.livestock_id || index}
                flexDirection="row"
                flex={1}
                display={"flex"}
                alignContent="center"
                alignItems="center"
                justifyContent="space-between"
                m={1}
                p={1}
                borderBottom={"1px solid #e0e0e0bf"}
                sx={{ cursor: "pointer" }}
                onClick={() => handleLivestockSelected(livestock)}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {getLivestockName(livestock)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
